/**
 * ⚖️ BBTV Quantity Formatter (Law UN-01 Authority)
 * 
 * Centralized logic for formatting ingredient quantities.
 * Handles "nice fractions" (1/2) vs "clean decimals" (1,2).
 * 
 * [SYNC NOTE] This file must be kept in sync with server/lib/quantityFormatter.js
 */

const NICE_FRACTION_MAP = {
    0.5: "1/2",
    0.25: "1/4",
    0.75: "3/4",
    0.33: "1/3",
    0.66: "2/3",
    0.125: "1/8"
};

/**
 * Checks if a value is near an integer within a given tolerance.
 */
export function isNearInteger(value, tolerance = 0.05) {
    if (value === undefined || value === null || isNaN(value)) {
        // V12.15: If it's a string, it's definitely not an integer-like numeric, so return false
        return false;
    }
    return Math.abs(value - Math.round(value)) < tolerance;
}

function getCoreQuantityString(amount) {
    if (amount === undefined || amount === null) return "0";

    // V12.15: If it's already a formatted hybrid string (e.g., "1 su bardağı + 2 yemek kaşığı"), skip numeric logic
    if (typeof amount === 'string' && (amount.includes('+') || isNaN(parseFloat(amount.replace(',', '.'))))) {
        return amount;
    }

    let num = isNaN(amount) ? parseFloat(amount.toString().replace(',', '.')) : amount;
    if (isNaN(num)) return "0";

    // Floating point correction (0.30000000004 -> 0.3)
    num = Number(num.toFixed(4));

    if (num < 0.001) return "0";

    const whole = Math.floor(num);
    const fraction = Number((num - whole).toFixed(4));

    // 1. Integer Check
    if (fraction < 0.001) return whole.toString();
    if (fraction > 0.999) return (whole + 1).toString();

    // 2. Nice Fraction Check
    for (const [val, str] of Object.entries(NICE_FRACTION_MAP)) {
        if (Math.abs(fraction - parseFloat(val)) < 0.01) {
            return whole > 0 ? `${whole} ${str}` : str;
        }
    }

    // 3. Decimal Fallback (No spaces! 1.2 -> "1,2")
    return num.toString().replace('.', ',');
}

/**
 * Formats a quantity as a string.
 * Priority: Integer -> Nice Fraction -> Decimal String (Comma sorted)
 */
export function formatQuantity(amount, originalText = null, scaleFactor = 1.0) {
    const sFactor = Number(scaleFactor) || 1.0;

    // ⚖️ MIRROR PRINCIPLE (Ayna İlkesi)
    if (Math.abs(sFactor - 1.0) < 0.001 && originalText && originalText !== 'Bilinmiyor') {
        const cleanedText = originalText.toString().trim();
        if (cleanedText) return cleanedText;
    }

    const result = getCoreQuantityString(amount);

    // 🛡️ UNIT PRESERVATION (Mirror Scaling)
    // Always preserve the unit part of originalText if it exists, ensuring scaling doesn't drop units
    if (originalText && originalText.length > 1) {
        const unitPart = originalText.replace(/^[\d\s\/\.,]+/, '').trim();
        // Ensure unitPart is actually a word/unit, not just more numbers
        if (unitPart && !/^\d+$/.test(unitPart)) {
            return `${result} ${unitPart}`.trim();
        }
    }

    return result;
}
